import { type ActionIdRegistry, stringUtils } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { withSuspense } from '@festina-life/ui/libs'
import type { CommandActionVariantOrSimulation } from '@festina-life/ui/theme'
import { useEffect } from 'react'
import { withErrorBoundary } from 'react-error-boundary'

import { getActionMetadata } from '../../hooks/navigation/categories/utils'
import { useComponent, useService } from '../../lenteHooks'
import { NullReturn } from '../actions/NullReturn'
import { useActionsContext } from './actionsContext'
import type { ActionData, CodedActionWarning } from './types'
import { isActionIdWorkflow } from './utils'

export type CommandModalButtonRegisterTemplateProps = {
  actionId: keyof ActionIdRegistry
  title: string
  registrationId: string
  entityId?: unknown
  isActionAvailable: boolean
  commandActionVariant: CommandActionVariantOrSimulation
}

type Props = CommandModalButtonRegisterTemplateProps & {
  warnings: CodedActionWarning[] | undefined
}

export const CommandModalButtonRegisterTemplate = ({
  actionId,
  title,
  registrationId,
  entityId,
  isActionAvailable,
  commandActionVariant,
  warnings,
}: Props) => {
  const translate = useTranslate()
  const { onRegister: onRegisterAction, onUnregister: onUnregisterAction } =
    useActionsContext()
  const actionMetadata = useService('logic.actionMetadata')
  const metadata = getActionMetadata(actionMetadata, actionId)
  const pretendCommands = useComponent(
    'pages.commandDataActions.life.collectionPersonPretendCommandPaletteButtons',
  )
  const isPretend =
    actionId.startsWith('life.') &&
    actionId.slice('life.'.length) in pretendCommands

  const categories = metadata.categories

  useEffect(() => {
    const status: ActionData['status'] = isActionAvailable
      ? { isAvailable: true, warnings }
      : {
          isAvailable: false,
          warnings,
          reasons: [{ code: 'insufficientAccess' }],
        }

    onRegisterAction({
      key: registrationId,
      translation: isPretend
        ? `${title} (${translate('command.isScenario')})`
        : title,
      categories,
      nestedStatus: entityId ? { entityId, status } : undefined,
      status,
      isVisible: stringUtils.strictIncludes(commandActionVariant, [
        'create',
        'simulationOnly',
      ]),
      actionId:
        commandActionVariant === 'simulationOnly'
          ? `simulationOnly.${actionId}`
          : actionId,
      entityId,
      isWorkflow: isActionIdWorkflow(actionId),
    })
  }, [
    entityId,
    onRegisterAction,
    actionId,
    registrationId,
    commandActionVariant,
    isActionAvailable,
    title,
    warnings,
    categories,
    isPretend,
    translate,
  ])

  // The unregister is on purpose seperate as if it was part of the other useEffect, the unregister -> register would run infinite
  useEffect(
    () => () => {
      onUnregisterAction(registrationId)
    },
    [onUnregisterAction, registrationId],
  )

  return null
}

export const CommandModalButtonRegisterTemplateWithErrorBoundary =
  withErrorBoundary(withSuspense(CommandModalButtonRegisterTemplate), {
    FallbackComponent: NullReturn, //We do not want to have any visible fallback
  })
